import { Link } from "react-router-dom";
import { usePageMeta } from "@/hooks/usePageMeta";

const features = [
  {
    icon: "ri-movie-2-line",
    title: "资源丰富",
    desc: "汇聚电影与电视剧两大类内容，覆盖动作、爱情、科幻、悬疑、古装等热门题材，满足不同观看口味。",
  },
  {
    icon: "ri-flashlight-line",
    title: "更新及时",
    desc: "紧跟院线与热播剧集节奏，持续补充最新上线的影视作品，让你不错过任何热门内容。",
  },
  {
    icon: "ri-hand-coin-line",
    title: "完全免费",
    desc: "无需注册、无需付费，打开网页即可浏览与观看，以最简单的方式享受影视资源。",
  },
  {
    icon: "ri-smartphone-line",
    title: "全端适配",
    desc: "电脑、平板与手机均可流畅访问，界面随屏幕自动调整，随时随地都能找到想看的作品。",
  },
];

const guides = [
  { step: "01", title: "浏览与检索", desc: "通过顶部导航进入电影或电视剧频道，或直接使用搜索框输入片名、类型、演员关键词。" },
  { step: "02", title: "筛选与排序", desc: "在左侧筛选栏按类型、地区、年份进行组合筛选，并可按最新上线、热门、评分排序。" },
  { step: "03", title: "查看详情", desc: "点击影视卡片进入详情页，查看剧情简介、演职人员与相关资料，电视剧还可切换剧集。" },
  { step: "04", title: "收藏与分享", desc: "在详情页一键收藏喜欢的作品，收藏记录保存在本机浏览器；也可复制链接分享给好友。" },
];

const contacts = [
  { icon: "ri-mail-line", label: "联系邮箱", value: "service@91shipin.example", href: "mailto:service@91shipin.example" },
  { icon: "ri-wechat-line", label: "微信公众号", value: "91视频免费看", href: "#" },
  { icon: "ri-weibo-line", label: "官方微博", value: "@91视频免费看", href: "https://weibo.com/" },
  { icon: "ri-telegram-line", label: "用户交流群", value: "t.me/v91shipin", href: "https://t.me/" },
];

export default function AboutPage() {
  usePageMeta(
    "关于我们 - 91视频免费看",
    "了解 91视频免费看的平台定位、服务特色与联系方式。本站为影视爱好者提供免费的高清电影与电视剧在线观看体验。"
  );

  return (
    <div className="w-full">
      <section className="w-full bg-primary-950">
        <div className="w-full max-w-[1400px] mx-auto px-4 md:px-8 py-12 md:py-16">
          <nav className="flex items-center gap-1.5 text-sm text-background-300 mb-5" aria-label="面包屑">
            <Link to="/" className="hover:text-accent-400 transition-colors">首页</Link>
            <span className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-right-s-line"></i></span>
            <span className="text-white">关于我们</span>
          </nav>
          <h1 className="font-heading text-3xl md:text-4xl font-bold text-white">关于 91视频免费看</h1>
          <p className="mt-4 text-sm md:text-base text-background-200 max-w-3xl leading-relaxed">
            91视频免费看是一个面向广大影视爱好者的免费视频影视资源平台，致力于让每一位观众都能轻松找到并观看
            热门的电影与电视剧作品。我们相信好内容不该有门槛，因此坚持免费、简洁、及时的产品理念，
            用心打磨每一次浏览与观看体验。
          </p>
        </div>
      </section>

      <div className="w-full max-w-[1400px] mx-auto px-4 md:px-8 py-12 space-y-14">
        <section>
          <div className="flex items-center gap-3 mb-6">
            <span className="w-1.5 h-7 rounded-full bg-accent-500"></span>
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">我们的特色</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
            {features.map((feature) => (
              <div key={feature.title} className="rounded-lg bg-background-100 border border-background-200 p-5 md:p-6">
                <span className="w-12 h-12 flex items-center justify-center rounded-lg bg-primary-500 text-white mb-4">
                  <i className={`${feature.icon} text-2xl`}></i>
                </span>
                <h3 className="font-heading text-base font-bold text-foreground-950">{feature.title}</h3>
                <p className="mt-2 text-sm text-foreground-600 leading-relaxed">{feature.desc}</p>
              </div>
            ))}
          </div>
        </section>

        <section>
          <div className="flex items-center gap-3 mb-6">
            <span className="w-1.5 h-7 rounded-full bg-accent-500"></span>
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">使用说明</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
            {guides.map((guide) => (
              <div key={guide.step} className="relative rounded-lg border border-background-200 p-5 md:p-6">
                <span className="font-heading text-3xl font-bold text-accent-500/70">{guide.step}</span>
                <h3 className="mt-2 font-heading text-base font-bold text-foreground-950">{guide.title}</h3>
                <p className="mt-2 text-sm text-foreground-600 leading-relaxed">{guide.desc}</p>
              </div>
            ))}
          </div>
        </section>

        <section>
          <div className="flex items-center gap-3 mb-6">
            <span className="w-1.5 h-7 rounded-full bg-accent-500"></span>
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">联系方式</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
            {contacts.map((contact) => (
              <a
                key={contact.label}
                href={contact.href}
                {...(contact.href.startsWith("http") ? { target: "_blank", rel: "noopener noreferrer nofollow" } : {})}
                className="group flex items-center gap-4 rounded-lg bg-secondary-50 border border-secondary-200 p-5 hover:border-primary-300 transition-colors"
              >
                <span className="w-11 h-11 flex items-center justify-center rounded-lg bg-secondary-500 text-white shrink-0">
                  <i className={`${contact.icon} text-xl`}></i>
                </span>
                <div className="min-w-0">
                  <p className="text-xs text-foreground-500">{contact.label}</p>
                  <p className="mt-0.5 text-sm font-medium text-foreground-900 truncate group-hover:text-primary-600 transition-colors">
                    {contact.value}
                  </p>
                </div>
              </a>
            ))}
          </div>
        </section>

        <section className="rounded-lg bg-primary-50 border border-primary-100 p-6 md:p-10">
          <div className="flex items-center gap-3 mb-4">
            <span className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-500 text-white">
              <i className="ri-information-line text-xl"></i>
            </span>
            <h2 className="font-heading text-xl font-bold text-primary-900">免责声明</h2>
          </div>
          <div className="space-y-3 text-sm text-foreground-700 leading-relaxed">
            <p>
              本站所有影视资源的文字简介与图片素材均收集整理自互联网公开信息，仅供用户学习、交流与参考。
              本站不存储、不制作、不传播任何影视源文件，也不提供任何影视内容的上传与下载服务。
            </p>
            <p>
              本站页面中展示的播放区域仅为用户体验演示，尚未接入真实播放服务。
              如本站内容无意中侵犯了您的权益，请通过上方联系方式与我们取得联系，我们将在第一时间进行处理。
            </p>
            <p>
              用户在使用本站时，请遵守所在地区相关法律法规，合理、合法地使用网络资源。
              因使用本站内容所产生的任何后果，由用户自行承担。
            </p>
          </div>
        </section>
      </div>
    </div>
  );
}